import { useEffect, useState, forwardRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../../../lib/supabase'

const CardDisplay = forwardRef(({ supporter }, ref) => {
  const [isPaid, setIsPaid] = useState(false)
  const [logoUrl, setLogoUrl] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!supporter?.id) return

      // Check subscription
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('valid_until')
        .eq('supporter_id', supporter.id)
        .order('valid_until', { ascending: false })
        .limit(1)
        .single()
      if (sub && new Date(sub.valid_until) >= new Date()) setIsPaid(true)

      // Fetch club logo — always fetch directly by club_id, works from any context
      if (supporter?.club_id) {
        const { data: club } = await supabase
          .from('clubs')
          .select('logo_url')
          .eq('id', supporter.club_id)
          .single()
        if (club?.logo_url) setLogoUrl(club.logo_url)
      }
    }
    fetchData()
  }, [supporter])

  const initials = supporter?.full_name
    ?.split(' ').map((n) => n[0]).join('').toUpperCase()

  const joinedDate = new Date(supporter?.joined_at).toLocaleDateString('en-US', {
    month: 'short', year: 'numeric',
  })

  const expiryDate = new Date(
    new Date(supporter?.joined_at).setFullYear(
      new Date(supporter?.joined_at).getFullYear() + 1
    )
  ).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

  return (
    <div
      ref={ref}
      style={{
        width: '360px',
        background: 'linear-gradient(135deg, #052e16 0%, #14532d 50%, #15803d 100%)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
        border: '1px solid #166534',
        position: 'relative',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Holographic Strip */}
      {isPaid ? (
        <div style={{
          height: '12px',
          width: '100%',
          background: 'linear-gradient(90deg, #ff0080, #ff8c00, #ffe000, #00ff80, #00cfff, #cc00ff, #ff0080)',
          animation: 'shimmer 3s linear infinite',
          backgroundSize: '200% 100%',
        }} />
      ) : (
        <div style={{ height: '12px', width: '100%', background: '#1a3a2a' }} />
      )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
      `}</style>

      {/* Card Header */}
      <div style={{ padding: '20px 28px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#86efac', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px' }}>
            {supporter?.clubs?.name}
          </div>
          <div style={{ color: '#4ade80', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', marginTop: '4px', opacity: 0.7 }}>
            Official Supporter
          </div>
        </div>

        {/* Club Logo or fallback SVG */}
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Club Logo"
            crossOrigin="anonymous"
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        ) : (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="16" fill="#1f5c38"/>
            <path d="M16 6C10.477 6 6 10.477 6 16C6 21.523 10.477 26 16 26C21.523 26 26 21.523 26 16C26 10.477 21.523 6 16 6ZM16 8C17.38 8 18.69 8.28 19.88 8.79L17.5 11.17L14.5 11.17L12.12 8.79C13.31 8.28 14.62 8 16 8ZM10.5 10.5L12.5 12.5L11.5 15.5L9 16.5C9 16.5 8.5 14 10.5 10.5ZM21.5 10.5C23.5 14 23 16.5 23 16.5L20.5 15.5L19.5 12.5L21.5 10.5ZM16 13L18.5 14.5L17.5 17.5H14.5L13.5 14.5L16 13ZM9.5 18.5L12 17.5L14 19.5L13 22C11 21 9.5 18.5 9.5 18.5ZM22.5 18.5C22.5 18.5 21 21 19 22L18 19.5L20 17.5L22.5 18.5ZM16 24C14.62 24 13.31 23.72 12.12 23.21L14.5 20.83H17.5L19.88 23.21C18.69 23.72 17.38 24 16 24Z" fill="white"/>
          </svg>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: '#1e5c38', margin: '0 28px 20px' }} />

      {/* Avatar and Name */}
      <div style={{ padding: '0 28px', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#1a4a2a',
          border: '2px solid #2d6a42',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {supporter?.profile_photo ? (
            <img
              src={supporter.profile_photo}
              alt="Profile"
              crossOrigin="anonymous"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ color: 'white', fontWeight: '900', fontSize: '18px' }}>{initials}</span>
          )}
        </div>
        <div>
          <div style={{ color: 'white', fontWeight: '900', fontSize: '20px', textTransform: 'uppercase', letterSpacing: '-0.5px' }}>
            {supporter?.full_name}
          </div>
          <div style={{ color: '#86efac', fontSize: '11px', marginTop: '4px', opacity: 0.7 }}>
            {supporter?.chapters?.name} Chapter
          </div>
        </div>
      </div>

      {/* Membership ID */}
      <div style={{
        margin: '0 28px 20px',
        background: '#0a2518',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '4px',
      }}>
        <div>
          <div style={{ color: '#4ade80', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '4px', opacity: 0.7 }}>
            Membership ID
          </div>
          <div style={{ color: 'white', fontWeight: '900', fontSize: '18px', letterSpacing: '3px' }}>
            {supporter?.membership_id}
          </div>
        </div>
        <div style={{
          background: '#1a4a2a',
          padding: '4px 12px',
          color: '#86efac',
          fontSize: '11px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          borderRadius: '2px',
        }}>
          ACTIVE
        </div>
      </div>

      {/* Details Grid */}
      <div style={{ padding: '0 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Member Since', value: joinedDate },
          { label: 'Valid Until', value: expiryDate },
          { label: 'Gender', value: supporter?.gender || 'N/A' },
          { label: 'Region', value: supporter?.region || 'N/A' },
        ].map((item) => (
          <div key={item.label}>
            <div style={{ color: '#4ade80', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '4px', opacity: 0.7 }}>
              {item.label}
            </div>
            <div style={{ color: 'white', fontWeight: '600', fontSize: '13px', textTransform: 'capitalize' }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* QR Code + Seal */}
      <div style={{ padding: '0 28px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        {isPaid ? (
          <div style={{ width: '56px', height: '56px' }}>
            <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="28" cy="28" r="26" fill="#16a34a"/>
              <circle cx="28" cy="28" r="22" fill="none" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="3 2"/>
              <path d="M18 28l7 7 13-13" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ) : (
          <div style={{ width: '56px', height: '56px' }} />
        )}

        <div style={{ background: 'white', padding: '8px', borderRadius: '4px' }}>
          <QRCodeSVG
            value={`${window.location.origin}/verify/${supporter?.membership_id || 'DSMS'}`}
            size={56}
            bgColor="#ffffff"
            fgColor="#000000"
            level="M"
          />
        </div>
      </div>
    </div>
  )
})

CardDisplay.displayName = 'CardDisplay'
export default CardDisplay